export type FlushCallback<T> = (elements: T[]) => void;

// AutoFlushingQueue queues elements and flushes those elements periodically or when the max queue size is reached.
// flushCallback is invoked whenever the queue is flushed with the list of all of the currently enqueued elements.
export class AutoFlushingQueue<T> {
  private queue: T[] = [];
  private flushTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly flushIntervalMs: number,
    private readonly maxQueueSize: number,
    private readonly flushCallback: FlushCallback<T>
  ) {}

  push(element: T): void {
    this.queue.push(element);

    if (this.queue.length >= this.maxQueueSize) {
      this.flush();
    } else if (!this.flushTimeout) {
      this.scheduleFlush();
    }
  }

  flush(): void {
    if (this.queue.length === 0) {
      return;
    }

    clearTimeout(this.flushTimeout);
    this.flushTimeout = undefined;

    const elementsToFlush = this.queue;
    this.queue = [];

    this.flushCallback(elementsToFlush);
  }

  private scheduleFlush(): void {
    this.flushTimeout = setTimeout(() => {
      this.flush();
    }, this.flushIntervalMs);
  }

  destroy(): void {
    clearTimeout(this.flushTimeout);
    this.flushTimeout = undefined;
  }
}
